import type { Ref, ComputedRef } from "vue";

export const csrfTokenKey = ["csrfToken"];

/******** Group *********/
export const groupKeys = {
  all: ["groups"],
  lists: () => [...groupKeys.all, "list"],
  list: (filters: { [key: string]: any }) => [...groupKeys.lists(), { ...filters }],
  details: () => [...groupKeys.all, "detail"],
  detail: (groupId: string) => [...groupKeys.details(), groupId]
};

export const groupTypeKeys = {
  all: (groupId: string) => ["groupTypes", groupId]
};

export const groupShippingAddressesKeys = {
  all: (groupId: string) => ["groupShippingAddresses", groupId]
};

export const groupEnrollmentsKeys = {
  all: ["groupEnrollmentsKeys"], // ["groupEnrollmentsKeys"]
  lists: () => [...groupEnrollmentsKeys.all, "list"], // ["groupEnrollmentsKeys", "list"]
  list: (groupId: string, filters: { [key: string]: any }) => [
    ...groupEnrollmentsKeys.lists(),
    groupId,
    { ...filters }
  ], // ["groupEnrollmentsKeys", "list", 1, {}]
  details: () => [...groupEnrollmentsKeys.all, "detail"], // ["groupEnrollmentsKeys", "detail"]
  detail: (groupId: string, enrollmentId: string) => [
    ...groupEnrollmentsKeys.details(),
    groupId,
    enrollmentId
  ] // ["groupEnrollmentsKeys", "detail", 1, 2]
};

export const groupProductsKeys = {
  all: ["groupProductsKeys"], // ["groupProductsKeys"]
  lists: () => [...groupProductsKeys.all, "list"], // ["groupProductsKeys", "list"]
  list: (groupId: string, filters: { [key: string]: any }) => [
    ...groupProductsKeys.lists(),
    groupId,
    { ...filters }
  ], // ["groupProductsKeys", "list", 1, {}]
  details: () => [...groupProductsKeys.all, "detail"], // ["groupProductsKeys", "detail"]
  detail: (groupId: string, productId: string) => [
    ...groupProductsKeys.details(),
    groupId,
    productId
  ] // ["groupProductsKeys", "detail", 1, 2]
};

export const groupProductSeatsKeys = {
  all: ["groupProductSeatsKeys"], // ["groupProductSeatsKeys"]
  lists: () => [...groupProductSeatsKeys.all, "list"], // ["groupProductSeatsKeys", "list"]
  list: (
    groupId: string,
    productId: ComputedRef<string> | string,
    filters: { [key: string]: any }
  ) => [...groupProductSeatsKeys.lists(), groupId, productId, { ...filters }], // ["groupProductSeatsKeys", "list", 1, 2, {}]
  details: () => [...groupProductSeatsKeys.all, "detail"], // ["groupProductSeatsKeys", "detail"]
  detail: (groupId: string, productId: ComputedRef<string> | string, seatId: string) => [
    ...groupProductSeatsKeys.details(),
    groupId,
    productId,
    seatId
  ] // ["groupProductSeatsKeys", "detail", 1, 2, 3]
};

export const groupSeatAllocationKeys = {
  all: ["groupSeatAllocationKeys"],
  lists: () => [...groupSeatAllocationKeys.all, "list"],
  list: (groupId: string, productId: string, orderId: string) => [
    ...groupSeatAllocationKeys.lists(),
    groupId,
    productId,
    orderId
  ]
};

export const groupSeatAllocationFilterKeys = {
  all: ["groupSeatAllocationFilterKeys"],
  lists: () => [...groupSeatAllocationFilterKeys.all, "list"],
  list: (groupId: string, productId: string, orderId: string, filters: { [key: string]: any }) => [
    ...groupSeatAllocationFilterKeys.lists(),
    groupId,
    productId,
    orderId,
    { ...filters }
  ]
};

export const groupCourseResourcesKey = ["groupCourseResources"];

export const groupCTSOSettingsKey = ["groupCTSOSettings"];

/******** Organization *********/
export const orgKeys = {
  all: ["orgs"],
  lists: () => [...orgKeys.all, "list"],
  list: (filters: { [key: string]: any }) => [...orgKeys.lists(), { ...filters }],
  details: () => [...orgKeys.all, "detail"],
  detail: (orgId: string) => [...orgKeys.details(), orgId]
};

export const organizationBillingContactsKeys = {
  all: (organizationId: string) => ["organizationBillingContacts", organizationId]
};

export const internalDefaultOrganizationBillingContactKeys = {
  all: ["internalDefaultOrganizationBillingContact"],
  details: () => [...internalDefaultOrganizationBillingContactKeys.all, "detail"],
  detail: (organizationId: string) => [
    ...internalDefaultOrganizationBillingContactKeys.details(),
    organizationId
  ]
};

export const organizationGroupsKeys = {
  all: (orgId: string) => ["organizationGroupsKeys", orgId],
  lists: (orgId: string) => [...organizationGroupsKeys.all(orgId), "list"],
  list: (orgId: string, filters: { [key: string]: any }) => [
    ...organizationGroupsKeys.lists(orgId),
    { ...filters }
  ],
  details: (orgId: string) => [...organizationGroupsKeys.all(orgId), "detail"],
  detail: (orgId: string, groupId: string) => [
    ...organizationGroupsKeys.details(orgId),
    orgId,
    groupId
  ]
};

export const organizationProductsKeys = {
  all: ["organizationProductsKeys"],
  lists: () => [...organizationProductsKeys.all, "list"],
  list: (organizationId: string) => [...organizationProductsKeys.lists(), organizationId]
};

export const organizationSeatDistributionKeys = {
  all: ["organizationSeatDistributionKeys"],
  lists: () => [...organizationSeatDistributionKeys.all, "list"],
  list: (
    organizationId: string,
    productId: ComputedRef<string>,
    filters: { [key: string]: any }
  ) => [...organizationSeatDistributionKeys.lists(), organizationId, productId, { ...filters }]
};

export const organizationMembersKeys = {
  all: (orgId: string) => ["organizationMembersKeys", orgId],
  lists: (orgId: string) => [...organizationMembersKeys.all(orgId), "list"],
  list: (orgId: string, filters: { [key: string]: any }) => [
    ...organizationMembersKeys.lists(orgId),
    { ...filters }
  ],
  details: (orgId: string) => [...organizationMembersKeys.all(orgId), "detail"],
  detail: (orgId: string, memberId: string) => [
    ...organizationMembersKeys.details(orgId),
    orgId,
    memberId
  ]
};

export const internalActiveShippingAddressKeys = {
  all: ["internalActiveShippingAddress"],
  details: () => [...internalActiveShippingAddressKeys.all, "detail"],
  detail: (organizationId: string) => [
    ...internalActiveShippingAddressKeys.details(),
    organizationId
  ]
};

export const internalOrganizationProductsKeys = {
  all: ["internalOrganizationProductsKeys"],
  lists: () => [...internalOrganizationProductsKeys.all, "list"],
  list: (organizationId: string, filters: { [key: string]: any }) => [
    ...internalOrganizationProductsKeys.lists(),
    organizationId,
    { ...filters }
  ]
};

export const internalCartOrganizationsKeys = {
  all: ["internalCartOrganizationsKeys"],
  lists: () => [...internalCartOrganizationsKeys.all, "list"],
  list: (filters: { [key: string]: any }) => [
    ...internalCartOrganizationsKeys.lists(),
    { ...filters }
  ]
};

export const internalCartOrganizationUsersKeys = {
  all: ["internalCartOrganizationUsersKeys"],
  lists: () => [...internalCartOrganizationUsersKeys.all, "list"],
  list: (filters: { [key: string]: any }) => [
    ...internalCartOrganizationUsersKeys.lists(),
    { ...filters }
  ]
};

export const internalCartShippingAddressKeys = {
  all: ["internalCartShippingAddress"],
  details: () => [...internalCartShippingAddressKeys.all, "detail"],
  detail: (orderId: string | undefined) => [...internalCartShippingAddressKeys.details(), orderId]
};

export const internalCartBillingAddressKeys = {
  all: ["internalCartBillingAddress"],
  details: () => [...internalCartBillingAddressKeys.all, "detail"],
  detail: (orderId: Ref<string | undefined>) => [
    ...internalCartBillingAddressKeys.details(),
    orderId
  ]
};

export const internalCartOrganizationDistributionKeys = {
  all: ["internalCartOrganizationDistribution"],
  details: () => [...internalCartOrganizationDistributionKeys.all, "detail"],
  detail: (orderId: Ref<string | undefined>) => [
    ...internalCartOrganizationDistributionKeys.details(),
    orderId
  ]
};

export const licenseOrgsKey = ["licenseOrgs"];
export const orgTypesKey = ["orgTypes"];
export const customerIdTypesKey = ["customerIdTypes"];
export const cognitoUserPoolsKey = ["cognitoUserPools"];

/******** Member *********/
export const memberKeys = {
  all: (groupId: string) => ["members", groupId],
  lists: (groupId: string) => [...memberKeys.all(groupId), "list"],
  list: (groupId: string, filters: { [key: string]: any }) => [
    ...memberKeys.lists(groupId),
    { ...filters }
  ],
  details: (groupId: string) => [...memberKeys.all(groupId), "detail"],
  detail: (groupId: string, userId: string) => [...memberKeys.details(groupId), userId]
};

/******** SubGroup *********/
export const subgroupKeys = {
  all: (groupId: string) => ["subgroups", groupId],
  lists: (groupId: string) => [...subgroupKeys.all(groupId), "list"],
  list: (groupId: string, filters: { [key: string]: any }) => [
    ...subgroupKeys.lists(groupId),
    { ...filters }
  ]
};

/******** Catalog *********/
export const catalogKeys = {
  all: ["catalogs"],
  lists: () => [...catalogKeys.all, "list"],
  list: (filters: { [key: string]: any }) => [...catalogKeys.lists(), { ...filters }],
  details: () => [...catalogKeys.all, "detail"],
  detail: (catalogId: string) => [...catalogKeys.details(), catalogId]
};

/******** Ecommerce *********/
export const cartKey = ["cart"];
export const anonOrderKey = ["anonOrder"];
export const shippingOptionsKey = ["shippingOptions"];
export const licenseKeys = {
  all: (orgId: string) => ["licenses", orgId],
  lists: (orgId: string) => [...licenseKeys.all(orgId), "list"],
  list: (orgId: string, filters: { [key: string]: any }) => [
    ...licenseKeys.lists(orgId),
    { ...filters }
  ]
};
export const sfMarketsKey = ["sfMarkets"];

export const cartDistributingGroupsKeys = {
  all: ["cartDistributingGroupsKeys"],
  lists: () => [...cartDistributingGroupsKeys.all, "list"]
};

export const ordersFilterOrgsKey = ["ordersFilterOrgs"];
export const orderPermissionOrgsKey = ["orderPermissionOrgs"];
export const orderSeatsKeys = {
  all: ["orderSeats"],
  details: () => [...orderSeatsKeys.all, "detail"],
  detail: (orderId: string) => [...orderSeatsKeys.details(), orderId]
};
export const orderKeys = {
  all: ["orders"],
  lists: () => [...orderKeys.all, "list"],
  list: (filters: { [key: string]: any }) => [...orderKeys.lists(), { ...filters }],
  details: () => [...orderKeys.all, "detail"],
  detail: (orderId: string | Ref<string>) => [...orderKeys.details(), orderId],
  statuses: () => [...orderKeys.all, "status"],
  status: (orderId: string) => [...orderKeys.statuses(), orderId]
};

export const internalOrderFilterOrgsKeys = {
  all: ["internalOrderFilterOrgs"],
  lists: () => [...internalOrderFilterOrgsKeys.all, "list"],
  list: (filters: { [key: string]: any }) => [
    ...internalOrderFilterOrgsKeys.lists(),
    { ...filters }
  ]
};

export const internalOrderEligbilityKeys = {
  all: ["orderEligibilityKeys"],
  details: () => [...internalOrderEligbilityKeys.all, "detail"],
  detail: (orderId: number) => [...internalOrderEligbilityKeys.details(), orderId]
};

export const internalOrderSalesTaxEligibilityKeys = {
  all: ["orderSalesTaxEligibilityKeys"],
  details: () => [...internalOrderSalesTaxEligibilityKeys.all, "detail"],
  detail: (orderId: number) => [...internalOrderSalesTaxEligibilityKeys.details(), orderId]
};

export const internalPurchaseOrderKeys = {
  all: ["internalPurchaseOrderKeys"],
  lists: () => [...internalPurchaseOrderKeys.all, "list"],
  list: (orderId: number, filters: { [key: string]: any }) => [
    ...internalPurchaseOrderKeys.lists(),
    orderId,
    { ...filters }
  ]
};

export const internalOrganizationNamesKeys = {
  all: ["internalOrganizationNamesKeys"],
  lists: () => [...internalOrganizationNamesKeys.all, "list"],
  list: (filters: { [key: string]: any }) => [
    ...internalOrganizationNamesKeys.lists(),
    { ...filters }
  ]
};

/******** Enrollments *********/
export const enrollmentKeys = {
  all: ["enrollments"],
  details: () => [...enrollmentKeys.all, "detail"],
  detail: (enrollmentId: string) => [...enrollmentKeys.details(), enrollmentId]
};

export const enrollmentAssessmentKeys = {
  all: ["enrollmentAssessmentKeys"],
  lists: () => [...enrollmentAssessmentKeys.all, "list"],
  list: (groupId: string, enrollmentId: Ref<string>, assessmentId: Ref<string>) => [
    ...groupProductSeatsKeys.lists(),
    groupId,
    enrollmentId,
    assessmentId
  ]
};

/******** Gradebook *********/
export const gradebookKeys = {
  all: (organizationId: number) => ["gradebooks", organizationId],
  lists: (organizationId: number) => [...gradebookKeys.all(organizationId), "list"],
  list: (organizationId: number, filters: { [key: string]: any }) => [
    ...gradebookKeys.lists(organizationId),
    { ...filters }
  ]
};

/******** User Management *********/
export const userKeys = {
  all: ["users"],
  lists: () => [...userKeys.all, "list"],
  list: (filters: { [key: string]: any }) => [...userKeys.lists(), { ...filters }],
  details: () => [...userKeys.all, "detail"],
  detail: (userId: string | ComputedRef<string>) => [...userKeys.details(), userId]
};

export const organizationsListKeys = ["organizationsListKeys"];

/******** Authentication *********/
export const passwordKey = ["password"];
export const userShippingAddressKey = ["userShippingAddress"];

/******** Utilities *********/
export const securityQuestionsKey = ["securityQuestions"];
export const instructorActionsKey = ["instructorActions"];
